import { useContext, useState } from 'react';

import UserContext, { View } from '@atom/components/userDetail/UserContext';
import { useNavigation } from '@atom/hooks/useNavigation';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import defaultProfileImage from '@atom/images/defaultProfileImage.png';
import { Icon, IconButton } from '@atom/mui';
import {
  AccountCircleIcon,
  ArrowBackIcon,
  CardMembershipIcon,
  FolderIcon,
  GroupIcon,
  type IconComponent,
  InfoIcon,
  MonetizationOnIcon,
  NotInterestedIcon,
  SettingsIcon,
  VpnKeyIcon,
  WorkIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  hasRolePermissions,
  hasSuperiorOrEqualUserLevel,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import userUtilities from '@atom/utilities/userUtilities';

import DeactivateUserModal from './DeactivateUserModal';
import NavigationButton from './NavigationButton';

import classes from './userHeader.module.css';

interface ViewDetail {
  value: View;
  label: string;
  icon: IconComponent;
}
const UserGroupHeader = () => {
  const [, { navigate, backDestination }] = useNavigation();
  const userProfile = useUserProfile();
  const { user } = useContext(UserContext);

  const [deactivateOpen, setDeactivateOpen] = useState<boolean>(false);

  const isOwnProfile = userProfile.userId === user.id;

  const renderDeactivate =
    !isOwnProfile &&
    hasRolePermissions(ROLE_SETS.ORG_ADMIN) &&
    hasSuperiorOrEqualUserLevel(user.level);

  const isBudgetsDisabled =
    !isOwnProfile && !hasRolePermissions(ROLE_SETS.MANAGER);

  const views: ViewDetail[] = [
    {
      value: View.INFO,
      label: 'info',
      icon: InfoIcon,
    },
    {
      value: View.GROUPS,
      label: 'groups',
      icon: GroupIcon,
    },
    {
      value: View.ROLES,
      label: 'roles',
      icon: AccountCircleIcon,
    },
    {
      value: View.BUDGETS,
      label: 'budgets',
      icon: MonetizationOnIcon,
    },
    {
      value: View.PERMISSIONS,
      label: 'permissions',
      icon: VpnKeyIcon,
    },
    {
      value: View.CERTIFICATIONS,
      label: 'certifications',
      icon: CardMembershipIcon,
    },
    {
      value: View.WORK,
      label: 'work',
      icon: WorkIcon,
    },
    {
      value: View.FILES,
      label: 'files',
      icon: FolderIcon,
    },
    {
      value: View.SETTINGS,
      label: 'settings',
      icon: SettingsIcon,
    },
  ];

  const isTabDisabled = (tab: ViewDetail) => {
    return tab.value === View.BUDGETS && isBudgetsDisabled;
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <div className={classes.topBar}>
          <div className={classes.titleContainer}>
            <IconButton onClick={() => navigate(backDestination('/team'))}>
              <Icon color={colors.neutral.white} icon={ArrowBackIcon} />
            </IconButton>
            <div className={classes.infoContainer}>
              <div className={classes.profileImageContainer}>
                <img
                  className={classes.profileImage}
                  src={user.photoUrl}
                  onError={(error: any) => {
                    error.target.src = defaultProfileImage;
                  }}
                />
              </div>
              <div className={classes.titleContent}>
                <div
                  className={classes.title}
                >{`${user.firstName} ${user.lastName}`}</div>
                <div>{user.email}</div>
                <div>{userUtilities.getUserRole(user.role)}</div>
              </div>
            </div>
          </div>
          <div>
            {renderDeactivate && (
              <IconButton onClick={() => setDeactivateOpen(true)}>
                <Icon color={colors.neutral.white} icon={NotInterestedIcon} />
              </IconButton>
            )}
          </div>
        </div>
        <div className={classes.navigationBar}>
          {views
            .reduce(
              (acc, view): ViewDetail[] =>
                isTabDisabled(view) ? acc : [...acc, view],
              [],
            )
            .map(view => (
              <NavigationButton key={view.value} view={view} />
            ))}
        </div>
      </div>
      <DeactivateUserModal
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        userId={user.id}
      />
    </>
  );
};

export default UserGroupHeader;
